import { useMemo } from "react";
import { useParams, useNavigate } from "react-router";
import { IconRefresh, IconChevronLeft } from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/ui/page-header";
import { EmptyState } from "@/components/ui/empty-state";
import { BoatSyncCard } from "@/components/boat-sync-card";
import {
  useTeamsSyncStatus,
  compareCommonCals,
  syncCommonCals,
} from "@/api/hooks/use-common-cals";
import { useNvpStatus, useEvent } from "@/api/hooks";
import { asEventId, asBoatCode } from "@/types/identifiers";

export function CommonCalsPage() {
  const { eventId: rawEventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const eventId = asEventId(rawEventId ?? "");

  const { data, isLoading, refetch, isRefetching } =
    useTeamsSyncStatus(eventId);
  const { data: event } = useEvent(eventId);
  const { getNvpStatus } = useNvpStatus();

  const compareFn = useMemo(() => compareCommonCals(eventId), [eventId]);
  const writeBackFn = useMemo(() => syncCommonCals(eventId), [eventId]);

  if (isLoading) {
    return (
      <div>
        <Skeleton className="h-8 w-48 rounded-lg" />
        <div className="mt-6 space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  const teams = data?.teams ?? [];

  return (
    <>
      <div className="mb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/events/${rawEventId}`)}
          className="text-muted-foreground"
        >
          <IconChevronLeft className="h-4 w-4 mr-1" />
          Back to Builds
        </Button>
      </div>

      <PageHeader
        title="Common Cals Sync"
        subtitle={event?.name}
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isRefetching}
          >
            <IconRefresh
              className={`h-4 w-4 mr-1 ${isRefetching ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
        }
      />

      <p className="text-sm text-muted-foreground mt-2 mb-6">
        View sync status for all boats and push common calibration values.
      </p>

      {teams.length === 0 ? (
        <EmptyState message="No boats found for this event" className="py-16" />
      ) : (
        <div className="flex flex-col gap-2">
          {[...teams]
            .sort((a, b) => a.boat_code.localeCompare(b.boat_code))
            .map((team) => (
              <BoatSyncCard
                key={team.boat_code}
                boatCode={asBoatCode(team.boat_code)}
                teamName={team.team_name}
                isoAlpha2={team.iso_alpha2}
                nvpStatus={getNvpStatus(team.boat_code)}
                syncType="common-cals"
                sourceAvailable={true}
                compareFn={compareFn}
                writeBackFn={writeBackFn}
                writeBackLabel="Sync"
                expectedHeader="Target"
              />
            ))}
        </div>
      )}
    </>
  );
}
